import {
  createRouter,
  createWebHistory,
} from 'vue-router';
import store from '@/store';
import adminRoutes from './admin';
import organisationRoutes from './organisation';
import partnerRoutes from './partner';
import sharedRoutes from './shared';
import publicRoutes from './public';
import reviewerRoutes from './reviewer';
import { setBreadcrumbsByRouteName } from '@/utils/breadcrumbs';
import { GLOBAL_PREFIX, LAYOUT_EMPTY } from '@/router/constants';
import { applyLocaleOverrideFromQuery, applyStoredLocaleOverride } from '@/i18n';
import { isPartnerOrganisation } from '@/utils/landing';

const routes = [
  ...publicRoutes,
  ...sharedRoutes,
  ...reviewerRoutes,
  ...adminRoutes,
  ...organisationRoutes,
  ...partnerRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/shared/NotFoundView.vue'),
    meta: {
      title: 'phrase.page_not_found',
      layout: LAYOUT_EMPTY,
      anonymousAllowed: true,
      hideBreadcrumbs: true,
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  linkActiveClass: 'active',
});

const CHUNK_RELOAD_STORAGE_KEY = 'ui-v2:chunk-load-reload-path';
const isChunkLoadError = (error) => (
  /loading chunk \d+ failed/i.test(error?.message || '')
  || /chunkloaderror/i.test(error?.name || '')
  || /failed to fetch dynamically imported module/i.test(error?.message || '')
  || /importing a module script failed/i.test(error?.message || '')
);

const userNeedsAgreement = (user = {}) => {
  const hasMembership = Boolean(user.has_organisation_membership)
    || (user.memberships?.length || 0) > 0;
  return hasMembership
    && (!user.last_agreed_to_terms || !user.last_agreed_to_data_privacy);
};

const hasUnverifiedPrimaryMembership = (user = {}) => (
  user?.org?.membership?.id
  && !user?.org?.membership?.is_verified
);

// Unverified, only the founder of a new organisation (its admin) may already
// register research. A colleague who joined an existing organisation by its
// name waits for verification on organisation home; the backend gives them
// nothing of that organisation until then.
const isUnverifiedFounder = (user = {}) => Boolean(user?.org?.membership?.is_admin);

const shouldRequireAgreement = (to) => (
  ['organisation-home', 'partner-home', 'organisation-chooser'].includes(to.name)
);

router.onError((error, to) => {
  if (!isChunkLoadError(error)) {
    return;
  }

  const targetPath = to?.fullPath || window.location.pathname;
  if (sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) === targetPath) {
    return;
  }

  sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, targetPath);
  window.location.assign(targetPath);
});

router.afterEach(() => {
  sessionStorage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
});

router.beforeEach(async (to, from, next) => {
  // Force the UI language for the rest of this tab session when the URL carries `?lang=`,
  // regardless of auth state — a research link should be able to set the respondent's
  // language even when they're never authenticated during the whole flow.
  applyLocaleOverrideFromQuery(to.query?.lang) || applyStoredLocaleOverride();

  const isSamePathNavigation = to.name === from.name && to.path === from.path;
  if (!to.meta.hideBreadcrumbs) {
    if (!isSamePathNavigation) {
      setBreadcrumbsByRouteName(
        to.name,
        {},
      );
    }
  } else {
    store.commit('setBreadcrumbs', null);
  }

  store.commit('setShowSideNav', false);

  const token = localStorage.getItem('auth-token');
  if (to.meta.anonymousAllowed) {
    if (token && !store.state.user.isAuthenticated) {
      await store.dispatch('refreshUser');
    }
    next();
  } else {
    if (!token) {
      store.commit('resetUser');

      if (to.meta.app === 'admin') {
        document.location.href = `${GLOBAL_PREFIX}/logout`;
        next(false);
        return;
      }

      if (to.meta.unauthenticatedRedirect) {
        const { company } = to.query;
        next({
          name: to.meta.unauthenticatedRedirect,
          query: { next: to.fullPath, ...(company ? { company } : {}) },
        });
        return;
      }

      // Met prefix: een kaal `/login` is zolang de ingress `/` nog naar ui-v1
      // wijst de loginpagina van de oude interface, en dan stapt iedereen die
      // een beschermde ui-v2-pagina opent daar uit. De loginpagina van ui-v2
      // leest `?next=` zelf en filtert open redirects (`getSafeInternalNextPath`),
      // dus er is niets dat ui-v1 hier nog moet doen.
      if (to.fullPath) {
        const liftParams = ['company'];
        const url = new URL(to.fullPath, 'http://x');
        const extra = liftParams
          .filter(p => url.searchParams.has(p))
          .map(p => `&${p}=${encodeURIComponent(url.searchParams.get(p))}`)
          .join('');
        document.location.href = `${GLOBAL_PREFIX}/login?next=`
          + encodeURIComponent(to.fullPath) + extra;
      } else {
        document.location.href = `${GLOBAL_PREFIX}/login`;
      }
      next(false);
      return;
    }
    if (!store.state.user.isAuthenticated) {
      await store.dispatch('refreshUser');
    }
    if (
      shouldRequireAgreement(to)
      && to.name !== 'common-user-agreement'
      && userNeedsAgreement(store.state.user)
    ) {
      next({
        name: 'common-user-agreement',
        query: { next: to.fullPath },
      });
      return;
    }
    // A partner's home is its brand overview, not the client dashboard; the
    // switcher and the breadcrumbs keep pointing at organisation-home.
    if (to.name === 'organisation-home' && isPartnerOrganisation(store.state.user.org)) {
      next({ name: 'partner-home' });
      return;
    }
    if (
      to.meta.app === 'organisation'
      && hasUnverifiedPrimaryMembership(store.state.user)
      && to.name !== 'organisation-home'
      && !(to.meta.allowUnverifiedMembership && isUnverifiedFounder(store.state.user))
    ) {
      next({ name: 'organisation-home' });
      return;
    }
    if (to.meta.app === 'admin' && !store.state.user.sust) {
      if (store.state.user.org) {
        next({ name: 'organisation-home' });
      } else {
        document.location.href = `${GLOBAL_PREFIX}/logout`;
        next(false);
      }
      return;
    }
    // The registration steps after the start page need an organisation to
    // register for; request-research tells someone without one why not.
    if (to.meta.requiresOrganisation && !store.state.user.org) {
      next({ name: 'request-research' });
      return;
    }
    if (to.meta.requiresStaff && !store.state.user.sust) {
      next({ name: 'organisation-home' });
      return;
    }
    next();
  }
});

export default router;
